import { Link, useLoaderData } from 'react-router'
import { PayloadFooter, PayloadHeader } from '#app/routes/_marketing+/index.tsx'
import { MediaImage } from '#app/components/image.tsx'
import { getBlogPost } from '#app/utils/blog.server.ts'
import {
	createWebPageSchema,
	organizationSchema,
	webSiteSchema,
} from '#app/utils/constants.ts'
import { type Route } from './+types/$slug.ts'
import { mediaAsset } from '#app/utils/media.ts'

export const handle = { chrome: 'payload' }

export async function loader({ params }: Route.LoaderArgs) {
	const post = await getBlogPost(params.slug)

	if (!post) {
		throw new Response('Not Found', { status: 404 })
	}

	return { post }
}

export const meta: Route.MetaFunction = ({ data, matches, params }) => {
	const post = data?.post
	const rootData = matches[0]?.data as
		| { requestInfo?: { origin: string; path: string } }
		| undefined
	const origin = rootData?.requestInfo?.origin ?? 'https://dealerproedge.com'
	const path = rootData?.requestInfo?.path ?? `/blog/${params.slug}`
	const canonicalUrl = `${origin}${path}`

	if (!post) {
		return [{ title: 'Post not found | DealerProEdge' }]
	}

	const pageSchema = createWebPageSchema({
		name: post.title,
		description: post.description,
		url: canonicalUrl,
	})
	const publishedAt = new Date(post.date).toISOString()
	const imageUrl = new URL(post.image, origin).href
	const blogPostingSchema = {
		'@context': 'https://schema.org',
		'@type': 'BlogPosting',
		author: {
			'@type': 'Organization',
			name: post.author,
			url: origin,
		},
		dateModified: publishedAt,
		datePublished: publishedAt,
		description: post.description,
		headline: post.title,
		image: imageUrl,
		keywords: post.keyword,
		mainEntityOfPage: {
			'@id': canonicalUrl,
			'@type': 'WebPage',
		},
		publisher: {
			'@type': 'Organization',
			logo: {
				'@type': 'ImageObject',
				url: mediaAsset('/favicons/512x512.png'),
			},
			name: 'DealerProEdge',
			url: origin,
		},
		url: canonicalUrl,
	}
	const breadcrumbSchema = {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{
				'@type': 'ListItem',
				item: origin,
				name: 'Home',
				position: 1,
			},
			{
				'@type': 'ListItem',
				item: `${origin}/blog`,
				name: 'Blog',
				position: 2,
			},
			{
				'@type': 'ListItem',
				item: canonicalUrl,
				name: post.title,
				position: 3,
			},
		],
	}

	return [
		{ title: post.title },
		{ name: 'description', content: post.description },
		{ name: 'keywords', content: post.keyword },
		{ name: 'twitter:title', content: post.title },
		{ name: 'twitter:description', content: post.description },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:image', content: imageUrl },
		{ property: 'og:title', content: post.title },
		{ property: 'og:description', content: post.description },
		{ property: 'og:type', content: 'article' },
		{ property: 'og:url', content: canonicalUrl },
		{ property: 'og:image', content: imageUrl },
		{ property: 'article:published_time', content: publishedAt },
		{ property: 'article:modified_time', content: publishedAt },
		{ property: 'article:author', content: post.author },
		{ tagName: 'link', rel: 'canonical', href: canonicalUrl },
		{ 'script:ld+json': organizationSchema },
		{ 'script:ld+json': webSiteSchema },
		{ 'script:ld+json': pageSchema },
		{ 'script:ld+json': blogPostingSchema },
		{ 'script:ld+json': breadcrumbSchema },
	]
}

export default function BlogPostRoute() {
	const { post } = useLoaderData<typeof loader>()

	return (
		<div className="dpe-live-page dpe-live-page--light dpe-blog-page">
			<PayloadHeader />
			<main>
				<article className="dpe-blog-post">
					<div className="dpe-blog-post-grid">
						<aside className="dpe-blog-post-sidebar">
							<div className="dpe-blog-post-sidebar-inner">
								<span className="dpe-blog-post-author-label">Author</span>
								<div className="dpe-blog-post-author">
									<MediaImage
										alt=""
										className="dpe-blog-post-author-avatar"
										height={192}
										layout="fixed"
										src="/favicons/192x192.png"
										width={192}
									/>
									<span className="dpe-blog-post-author-name">
										{post.author}
									</span>
								</div>
							</div>
						</aside>

						<div className="dpe-blog-post-main">
							<div className="dpe-blog-post-titlewrap">
								<nav
									aria-label="Breadcrumbs"
									className="dpe-blog-post-breadcrumbs"
								>
									<Link className="dpe-blog-post-back" to="/blog">
										← Blog
									</Link>
									<span className="dpe-blog-post-divider">/</span>
									<time>{post.date}</time>
								</nav>
								<h1 className="dpe-blog-post-title">{post.title}</h1>
							</div>

							<div className="dpe-blog-post-hero-image">
								<MediaImage
									alt=""
									aspectRatio={16 / 9}
									layout="fullWidth"
									priority
									src={post.image}
								/>
							</div>

							<div className="dpe-blog-post-excerpt">
								<p>{post.description}</p>
							</div>

							<div
								className="dpe-blog-post-body"
								dangerouslySetInnerHTML={{ __html: post.contentHtml }}
							/>
						</div>
					</div>
				</article>
			</main>
			<PayloadFooter />
		</div>
	)
}
